import { useApiWithAuth } from "@/modules/api";
import { PermissionPayload } from "@/modules/auth";
import { ClientePayload } from "@/modules/business/cliente/clienteBusiness";
import { clearObject } from "@/modules/utils";
import { formatCpfCnpj } from "../rntrcTypes";

/** De onde vieram os dados que preencheram a tela: cadastro da ANTT ou da GovPass. */
export type OrigemPreenchimentoAntt = "ANT" | "GOV";

/**
 * Pessoa do cadastro próprio da ANTT (transportador, proprietário, arrendatário). Só CPF/CNPJ e
 * nome são obrigatórios; o resto são os dados do novo cadastro na ANTT (TAC/ETC).
 */
export interface PessoaAnttPayload {
  idPessoaAntt?: number | null;
  cliente?: ClientePayload | null;
  cpfCnpj?: string | null;
  nome?: string | null;
  rg?: string | null;
  orgaoEmissorIdentidade?: string | null;
  ufIdentidade?: string | null;
  /** yyyy-mm-dd */
  dataNascimento?: string | null;
  sexo?: string | null;
  cnhNumero?: string | null;
  cnhCategoria?: string | null;
  possuiCnh?: boolean | null;
  keyBlCnhTemp?: string | null;
  enderecoTipo?: string | null;
  enderecoCep?: string | null;
  enderecoLogradouro?: string | null;
  enderecoNumero?: string | null;
  enderecoComplemento?: string | null;
  enderecoBairro?: string | null;
  telefone?: string | null;
  email?: string | null;
  /** PJ: gestor (sócio), outra pessoa ANTT. */
  gestor?: PessoaAnttPayload | null;
  tipoVinculoGestor?: string | null;
  origem?: OrigemPreenchimentoAntt | null;
  flExclusao?: boolean | null;
  dataCadastro?: Date | null;
  dataAlteracao?: Date | null;
  permissionInfo?: PermissionPayload;
}

export interface PessoaAnttFilterPayload {
  idCliente?: number | null;
  cpfCnpj?: string | null;
  nome?: string | null;
  arquivado?: boolean | null;
}

export const usePessoaAnttService = () => {
  const { data, loading, get, post, put, patch } = useApiWithAuth();

  const searchPessoaAntt = (
    filter: PessoaAnttFilterPayload,
    page: number
  ): Promise<void> => {
    return get("/rntrc/pessoa-antt", { ...clearObject({ ...filter }), page });
  };

  const getPessoaAntt = (idPessoaAntt: number): Promise<void> => {
    return get(`/rntrc/pessoa-antt/${idPessoaAntt}`);
  };

  const newPessoaAntt = (idCliente?: number | null): Promise<void> => {
    return get("/rntrc/pessoa-antt/new", idCliente ? { idCliente } : {});
  };

  /** A pessoa ANTT do documento, ou o que a GovPass tem dele (só do próprio cliente). */
  const preencherPessoaAntt = (
    cpfCnpj: string,
    idCliente?: number | null
  ): Promise<void> => {
    return get(
      "/rntrc/pessoa-antt/preencher",
      clearObject({ cpfCnpj, idCliente }),
      {
        skipEmptyToast: true
      } as Record<string, unknown>
    );
  };

  /**
   * Lupa: pessoas da base do cliente por nome ou início do CPF/CNPJ — cadastro da ANTT primeiro,
   * depois os transportadores da GovPass que ainda não estão nele.
   */
  const buscarPessoasParaPreenchimento = (
    termo: string,
    idCliente?: number | null
  ): Promise<void> => {
    return get(
      "/rntrc/pessoa-antt/preencher/busca",
      clearObject({ termo, idCliente }),
      {
        skipEmptyToast: true
      } as Record<string, unknown>
    );
  };

  const saveNewPessoaAntt = (entity: PessoaAnttPayload): Promise<void> => {
    return post("/rntrc/pessoa-antt", entity);
  };

  const updatePessoaAntt = (
    idPessoaAntt: number,
    entity: PessoaAnttPayload
  ): Promise<void> => {
    return put(`/rntrc/pessoa-antt/${idPessoaAntt}`, entity);
  };

  const shelvePessoaAntt = (idPessoaAntt: number): Promise<void> => {
    return patch(`/rntrc/pessoa-antt/${idPessoaAntt}/arquivar`);
  };

  const unshelvePessoaAntt = (idPessoaAntt: number): Promise<void> => {
    return patch(`/rntrc/pessoa-antt/${idPessoaAntt}/desarquivar`);
  };

  return {
    data,
    loading,
    searchPessoaAntt,
    getPessoaAntt,
    newPessoaAntt,
    preencherPessoaAntt,
    buscarPessoasParaPreenchimento,
    saveNewPessoaAntt,
    updatePessoaAntt,
    shelvePessoaAntt,
    unshelvePessoaAntt
  };
};

export const safePessoaAntt = (
  entity?: PessoaAnttPayload | null
): PessoaAnttPayload => {
  return entity ?? { cpfCnpj: "", nome: "" };
};

export const safeFilterPessoaAntt = (): PessoaAnttFilterPayload => {
  return { cpfCnpj: "", nome: "", arquivado: false };
};

export const getNomePessoaAntt = (entity?: PessoaAnttPayload | null) => {
  if (!entity || !entity.cpfCnpj) {
    return entity?.nome ?? "";
  }
  return (
    formatCpfCnpj(entity.cpfCnpj) + (entity.nome ? " - " + entity.nome : "")
  );
};
